import { supabase, TABLES } from "../lib/supabase";
import { createWorker } from "./workers";
import type { Database } from "../pages/types/supabase";
import { getCurrentWorkerProfile } from "./current-worker";

type WorkerInsert = Database["public"]["Tables"]["workers"]["Insert"];

interface InviteUserData {
  email: string;
  firstName: string;
  lastName: string;
  department?: string;
  phone?: string;
  role_id?: number;
  commission_level_id?: number;
}

/**
 * Invites a new user to the system by creating a worker record and sending an invitation
 */
export async function inviteUser(userData: InviteUserData) {
  if (!userData.email || !userData.firstName || !userData.lastName) {
    throw new Error("Email, first name, and last name are required");
  }

  // Ensure role_id is provided as it's mandatory for worker creation
  if (typeof userData.role_id !== "number") {
    throw new Error(
      "Role ID is required and must be a number to invite a user."
    );
  }

  if (typeof userData.commission_level_id !== "number") {
    throw new Error("Commission level is required to invite a user.");
  }

  try {
    const currentWorker = await getCurrentWorkerProfile();

    // Step 1: Create a worker record without the user_id
    const workerData: WorkerInsert = {
      full_name: `${userData.firstName} ${userData.lastName}`,
      email: userData.email,
      organization: currentWorker.organization,
      role_id: userData.role_id, // Now guaranteed to be a number
      commission_level_id: userData.commission_level_id,
    };

    const worker = await createWorker(workerData);

    // Step 2: Send invitation using Supabase Edge Function
    const { VITE_SUPABASE_URL } = import.meta.env;
    const functionUrl = `${VITE_SUPABASE_URL}/functions/v1/invite-user`;

    // Get the current user's session
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData.session?.access_token;

    if (!accessToken) {
      throw new Error("No active session found. Please log in again.");
    }

    try {
      console.log(`Sending invitation to: ${userData.email}`);
      const response = await fetch(functionUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          email: userData.email,
          redirectTo: `${window.location.origin}/auth/set-password`,
        }),
      });

      const responseData = await response.json();
      if (!response.ok) {
        console.error("Invitation error response:", responseData);
        throw new Error(
          responseData.message ||
            responseData.details ||
            "Failed to send invitation"
        );
      }

      console.log("Invitation sent successfully:", responseData);
      return {
        success: true,
        worker,
      };
    } catch (error) {
      // If there's an error sending the invitation, delete the worker record
      console.error("Error sending invitation:", error);
      await supabase.from(TABLES.WORKERS).delete().eq("id", worker.id);
      throw error;
    }
  } catch (error) {
    console.error("Error inviting user:", error);
    throw error;
  }
}
